import React, { ReactNode, useEffect, useState, useMemo } from 'react';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import useFeatures from './hooks/useFeatures';
import { PropertyField } from '../../../system/field';
import Section from './components/ListingSections';
import {
  ReviewFieldsButton,
  SaveButton,
  SidePanel,
  SwitchableItem,
} from './components/ListingComponents';
import { MappedField } from './components/MappedField';
import {
  ListingAssistantItem,
  ListingContainer,
} from './components/ListingContainer';
import { useSidePanel } from '../../../contexts/SidePanelContext';
import {
  DraftListingSection,
  useDraftListing,
} from '../../../contexts/DraftListingContext';
import { useMetadata } from '../../../contexts/MetadataContext';
import LoadingState from './components/LoadingState';
import useListingAssistant from './hooks/useListingAssistant';
import {
  Field,
  ListingRecord,
  ListingSection,
  RecordValue,
} from './utils/types';
import FormAccordion from '../../common/FormAccordion';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../flags';
import { DebugPanel, DebugFab, useDebugPanel } from '../debug';
import { generateDataTestId } from '../../../utils';
import {
  useListingField,
  useListingStatus,
  ListingResource,
} from '@brightmls/nextgen.lae.flc';
import { useTenant } from '../../../contexts/TenantContext';

const UnselectedItemsText: React.FC<any> = (props) => (
  <Typography variant="body1" sx={{ color: '#767678', mt: 1, mb: 2 }}>
    {props.text || 'No additional values for this source'}
  </Typography>
);

const ListingAssistant: React.FC = () => {
  const { showPanel, CustomDrawer } = useSidePanel();
  const [isReadyToImportOpen, setIsReadyToImportOpen] = useState(false);
  const [isNeedsReviewOpen, setIsNeedsReviewOpen] = useState(false);
  const [isAdditionalRecordsExpanded, setIsAdditionalRecordsExpanded] =
    useState(false);
  const { show, assistant, isLoadingSuggestions } = useDraftListing();
  const { template } = useMetadata();
  const listing = useListingAssistant();
  const [features] = useFeatures();
  const { hasFeature } = useFeatureFlags();
  const { getConfigValue } = useTenant();

  // Pluck tenant configuration
  const isAiServicesEnabled = getConfigValue<boolean>('ENABLE_AI_SERVICES');

  // Subscribe to property type field
  const propertyTypeField = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );

  // Status management
  const touchedStatus = useListingStatus('touched', false);

  const showDebugPanel = hasFeature(FeatureFlags.NGW_DEBUG_CREATE);
  const {
    isOpen: isDebugOpen,
    togglePanel: toggleDebugPanel,
    closePanel: closeDebugPanel,
  } = useDebugPanel({ assistantState: listing.state });

  // Split records into primary (most recent of each type) and additional
  const { primaryRecords, additionalRecords } = useMemo(() => {
    const allRecords = listing.getRecords((record: any) => record);

    // Group records by type
    const recordsByType: { [key: string]: any[] } = {};
    allRecords.forEach((record: any) => {
      const type = record.type || 'UNKNOWN';
      if (!recordsByType[type]) {
        recordsByType[type] = [];
      }
      recordsByType[type].push(record);
    });

    // Get most recent of each type (already sorted by date in getRecords)
    const primary: any[] = [];
    const additional: any[] = [];

    Object.entries(recordsByType).forEach(([type, records]) => {
      if (records.length > 0) {
        primary.push(records[0]); // Most recent
        additional.push(...records.slice(1)); // Rest
      }
    });

    return { primaryRecords: primary, additionalRecords: additional };
  }, [listing]);

  const unselectedFields = () => {
    const sectionRender = (
      section: ListingSection,
      relatedRecordId: string,
    ) => (
      <Section key={section.key} section={section}>
        {(field: Field) => (
          <MappedField
            field={field}
            relatedRecordId={relatedRecordId}
            key={field.key}
            variant="add"
            onClick={(field: Field, recordValue: RecordValue) => {
              listing.addField(field, recordValue);
            }}
          />
        )}
      </Section>
    );

    const recordRender = (record: ListingRecord, sections: ReactNode[]) => (
      <Grid key={record.id} container>
        <Grid sx={{ mb: 0.5 }} size={12}>
          <Typography variant="body1" fontWeight="fontWeightBold">
            {record.label}
          </Typography>
        </Grid>
        <Grid sx={{ mb: 2 }} size={12}>
          {sections?.length ? sections : <UnselectedItemsText />}
        </Grid>
      </Grid>
    );

    return listing.unselectedFields(sectionRender, recordRender, () => (
      <UnselectedItemsText text="No unselected records" />
    ));
  };

  const getReadyToImportPanel = () => {
    return (
      <SidePanel>
        <Box>
          <Typography variant="h6" fontWeight="fontWeightBold">
            Selected Items
            <Typography variant="caption">
              {' '}
              ({listing.getTotal(listing.READY_TO_IMPORT)} total selected)
            </Typography>
          </Typography>
          {listing.mapSections((key: any, section: any) => {
            return (
              <Section key={section.key} section={section}>
                {(field: any) => (
                  <MappedField
                    field={field}
                    key={field.key}
                    variant="remove"
                    onClick={(field: Field, recordValue: RecordValue) => {
                      listing.removeField(field, recordValue);
                    }}
                  />
                )}
              </Section>
            );
          }, listing.READY_TO_IMPORT)}
        </Box>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" fontWeight="fontWeightBold" sx={{ my: 1 }}>
            Unselected Items
          </Typography>
          {unselectedFields()}
        </Box>
      </SidePanel>
    );
  };

  const getNeedsReviewPanel = () => {
    return (
      <SidePanel>
        <Box sx={{ pb: 3 }}>
          <Typography variant="h6">
            Fields you must review
            <Typography variant="caption" sx={{ color: 'grey.500' }}>
              {' '}
              ({listing.getTotal(listing.REMAINING_REVIEW_FIELDS)} remaining)
            </Typography>
          </Typography>
          {listing.mapFields((key: any, field: any) => {
            return (
              <MappedField
                defaultExpanded={key === 0}
                field={field}
                key={field.key}
                variant="options"
                onClick={(field: Field, recordValue: RecordValue) => {
                  listing.resolveField(field, recordValue);
                }}
              />
            );
          }, listing.NEEDS_REVIEW)}
        </Box>
        <Divider />
        <Box sx={{ py: 3 }}>
          <Typography variant="h6">
            Resolved by Listing Assistant
            <Typography variant="caption" sx={{ color: 'grey.500' }}>
              {' '}
              ({listing.getTotal(listing.NO_CONFLICT_FIELDS)} total)
            </Typography>
          </Typography>
          {listing.mapFields((key: any, field: any) => {
            return (
              <MappedField
                defaultExpanded={key === 0}
                field={field}
                key={field.key}
                variant="options"
                onClick={(field: Field, recordValue: RecordValue) => {
                  listing.resolveField(field, recordValue);
                }}
              />
            );
          }, listing.NO_CONFLICT_FIELDS)}
        </Box>
      </SidePanel>
    );
  };

  const onReadyToImportSave = () => {
    listing.saveFields();
    setIsReadyToImportOpen(false);
    touchedStatus.updateValue(false);
  };

  const onNeedsReviewSave = () => {
    listing.persistResolvedFields();
    setIsNeedsReviewOpen(false);
  };

  const handleReadyToImportPanel = () => {
    setIsReadyToImportOpen(true);
  };

  const handleNeedToReviewPanel = () => {
    setIsNeedsReviewOpen(true);
  };

  const getNeedToReviewItem = () => {
    const shouldReviewTotal = listing.getTotal(listing.REMAINING_REVIEW_FIELDS);
    const shouldReviewFields = shouldReviewTotal > 0;
    const text = shouldReviewFields
      ? `${shouldReviewTotal} fields require your input`
      : 'No fields to be reviewed';
    const icons = {
      warning: '/icons/warning.svg',
      ok: '/icons/check_circle.svg',
    };
    return {
      label: 'Needs your Review',
      key: 'review',
      component: (
        <ListingAssistantItem
          icon={shouldReviewFields ? icons.warning : icons.ok}
          text={text}
          button={
            shouldReviewFields && (
              <ReviewFieldsButton
                onClick={handleNeedToReviewPanel}
                textVariant="caption"
              />
            )
          }
        />
      ),
    };
  };

  const getReadyToImportItem = () => {
    const readyToImportTotal = listing.getTotal(listing.READY_TO_IMPORT);
    const shouldReviewTotal = listing.getTotal(listing.REMAINING_REVIEW_FIELDS);
    const shouldReviewFields = shouldReviewTotal > 0;
    const isListingTouched = touchedStatus.value;
    const icons = {
      warning: '/icons/warning.svg',
      ok: '/icons/check_circle.svg',
    };
    return {
      label: 'Ready to Import',
      key: 'import',
      component: (
        <ListingAssistantItem
          icon={
            isListingTouched && readyToImportTotal > 0
              ? icons.warning
              : icons.ok
          }
          text={`${readyToImportTotal === 0 ? 'No' : readyToImportTotal} fields are ready to import`}
          button={
            !!listing.getTotal(listing.READY_TO_IMPORT) && (
              <ReviewFieldsButton
                disabled={shouldReviewFields}
                onClick={handleReadyToImportPanel}
                textVariant="caption"
              />
            )
          }
        />
      ),
    };
  };

  const getAllFeatures = () => {
    return features.render((feature: any) => {
      return {
        label: feature.key.label || feature.field.Name,
        key: feature.key.key,
        component: (
          <SwitchableItem
            checked={feature.isChecked}
            label=""
            disabled={feature.disabled}
            onChange={() => {
              features.toggleFeature(feature.key.key);
            }}
          />
        ),
      };
    });
  };

  // Helper to render record item
  const renderRecordItem = (record: any) => ({
    ...record,
    component: (
      <SwitchableItem
        data-testid={generateDataTestId(record.key, undefined, 'checkboxField')}
        checked={record.isEnabled}
        label={`${record.totalFields} Fields Available`}
        onChange={() => {
          listing.toggleRecord(record);
        }}
      />
    ),
  });

  useEffect(() => {
    if (!propertyTypeField.value) return () => {};
    if (!isLoadingSuggestions && propertyTypeField.value) {
      const templateData = template(propertyTypeField.value);
      listing.setTemplate(propertyTypeField.value, templateData);
    }
  }, [propertyTypeField.value, isLoadingSuggestions]);

  useEffect(() => {
    if (assistant?.records && Array.isArray(assistant.records)) {
      listing.setRecords(assistant.records);
      features.setRecords(assistant.records);
    }
  }, [assistant?.records, isLoadingSuggestions]);

  const defaultExpanded = true;

  if (propertyTypeField.value && isLoadingSuggestions) {
    return (
      <FormAccordion
        title="Listing Assistant"
        defaultExpanded={defaultExpanded}
      >
        <LoadingState />
      </FormAccordion>
    );
  }

  if (!show(DraftListingSection.ListingAssistant)) {
    return null;
  }

  const copy = isAiServicesEnabled
    ? 'Choose tax, listing data, or AI recommendations to start your listing. Proceed to draft creation if no records are available'
    : 'Choose tax or listing data to start your listing. Proceed to draft creation if no records are available';

  if (!propertyTypeField.value) {
    return (
      <FormAccordion
        title="Listing Assistant"
        subtext={copy}
        defaultExpanded={defaultExpanded}
      >
        <Typography variant="h5" fontWeight="fontWeightBold">
          Select a property type to get started
        </Typography>
      </FormAccordion>
    );
  }

  return (
    <FormAccordion
      title="Listing Assistant"
      subtext={copy}
      defaultExpanded={defaultExpanded}
    >
      <Grid container>
        <Grid container size={12}>
          {!!assistant?.records.length && (
            <>
              {/* Primary Records - Always Visible */}
              <Grid size={12}>
                <ListingContainer
                  title="Available Records"
                  items={primaryRecords.map(renderRecordItem)}
                />
              </Grid>

              {/* Additional Records Button */}
              {additionalRecords.length > 0 && (
                <Grid container size={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'start',
                      mt: 1,
                      ml: -1,
                      mb: 1,
                    }}
                  >
                    <Button
                      onClick={() =>
                        setIsAdditionalRecordsExpanded(
                          !isAdditionalRecordsExpanded,
                        )
                      }
                      endIcon={
                        isAdditionalRecordsExpanded ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )
                      }
                      sx={{
                        color: 'secondary.main',
                        textTransform: 'none',
                        fontWeight: '600',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                        },
                        '&:active': {
                          backgroundColor: 'transparent',
                        },
                      }}
                      disableRipple
                    >
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          lineHeight: 1.5,
                        }}
                      >
                        {`${isAdditionalRecordsExpanded ? 'Hide' : 'View'} Additional Records`}
                      </Typography>
                    </Button>
                  </Box>

                  {/* Expanded Additional Records */}
                  {isAdditionalRecordsExpanded && (
                    <Grid container size={12}>
                      <ListingContainer
                        items={additionalRecords.map(renderRecordItem)}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          )}

          <Grid size={12}>
            <ListingContainer
              title="Available Data"
              items={[getNeedToReviewItem(), getReadyToImportItem()]}
            />
          </Grid>

          {features.detectedListingFeatures.length > 0 && (
            <Grid size={12}>
              <ListingContainer
                title="Detected Listing Features"
                items={getAllFeatures()}
                isFeatures
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      <CustomDrawer
        title="Ready To Import"
        content={getReadyToImportPanel()}
        isOpen={isReadyToImportOpen}
        hidePanel={() => {
          setIsReadyToImportOpen(false);
        }}
        footer={
          <Box>
            <SaveButton onClick={onReadyToImportSave} disabled={false} />
          </Box>
        }
      />

      <CustomDrawer
        title="Need Your Review"
        content={getNeedsReviewPanel()}
        isOpen={isNeedsReviewOpen}
        hidePanel={() => {
          setIsNeedsReviewOpen(false);
        }}
        footer={
          <Box>
            <SaveButton
              disabled={listing.getTotal(listing.REMAINING_REVIEW_FIELDS) > 0}
              onClick={onNeedsReviewSave}
            />
          </Box>
        }
      />

      {/* Debug Panel Integration */}
      {showDebugPanel && (
        <>
          <DebugFab onClick={toggleDebugPanel} />
          <DebugPanel
            open={isDebugOpen}
            onClose={closeDebugPanel}
            assistantState={listing.state}
          />
        </>
      )}
    </FormAccordion>
  );
};

export default ListingAssistant;
